// ==================== ENUMS ====================
export enum PersonaEnum {
	SALARIED = 'SALARIED',
	SELF_EMPLOYED = 'SELF_EMPLOYED',
	// FREELANCER = 'FREELANCER'
}

export enum SectorEnum {
	GovernmentCivilian = 'GOVERNMENT_CIVILIAN',
	Retired = 'RETIRED',
	SemiGovernment = 'SEMI_GOVERNMENT',
	GovernmentMilitary = 'GOVERNMENT_MILITARY',
	ListedCompany = 'LISTED_COMPANY',
	Bankers = 'BANKERS',
	Private = 'PRIVATE',
	Freelancer = 'FREELANCER',
	SelfEmployed = 'SELF_EMPLOYED'
}

export enum ProductEnum {
	STANDARD = 'STANDARD',
	SELF_CONSTRUCT = 'SELF_CONSTRUCT',
	OFF_PLAN = 'OFF_PLAN'
}

export enum MaritalStatusEnum {
	Single = 'SINGLE',
	Married = 'MARRIED',
	Divorced = 'DIVORCED',
	Widowed = 'WIDOWED'
}

export enum ResidentialTypeEnum {
	HomeOwner = 'HOME_OWNER',
	WithFamily = 'WITH_FAMILY',
	Rented = 'RENTED'
}

export enum GenderEnum {
	Male = 'MALE',
	Female = 'FEMALE'
}

export enum AdditionalIncomeTypeEnum {
	PRIVATE_BUSINESS = 'PRIVATE_BUSINESS',
	INCENTIVES = 'INCENTIVES',
	RENTAL = 'RENTAL',
	SECOND_JOB = 'SECOND_JOB',
	PENSION = 'PENSION'
}

export enum EligibilityStatusEnum {
	Eligible = 'ELIGIBLE',
	NotEligible = 'NOT_ELIGIBLE'
}

export enum NotEligibleReasonType {
	PERMANENT = 'PERMANENT',
	TEMPORARY = 'TEMPORARY'
}

// ==================== INTERFACES ====================

export interface DateOfBirth {
	gregorian: string; // YYYY-MM-DD format
	hijri?: string; // YYYY-MM-DD format
}

export interface PersonalInformation {
	dateOfBirth: DateOfBirth;
	gender: GenderEnum;
	firstName: string;
	familyName: string;
	mobileNumber: string; // 9-15 digits without country code
}

export interface Expenses {
	rent: number;
	foodAndBeverages: number;
	education: number;
	healthCare: number;
	utilities: number;
	transportation: number;
	insurance: number;
	futureExpenses: number;
	others: number;
}

export interface Property {
	propertyId: string;
	productType: ProductEnum;
	price: number;
}

export interface RequestedFinancialParameters {
	downPayment: number;
	tenor: number;
	includeSubsidy: boolean;
	isFirstHome: boolean;
	subsidyAmount: number;
}

export interface ApplicantData {
	nationalId: string;
	nationality: string;
	profileId: string;
	sector: SectorEnum;
	persona: PersonaEnum;
	personalInformation: PersonalInformation;
	maritalStatus: MaritalStatusEnum;
	residentialType: ResidentialTypeEnum;
	numberOfDependents: number;
	numberOfDependentsInSchool: number;
	numberOfCarsOwned: number;
	expenses: Expenses;
	additionalIncome?: {
		type: AdditionalIncomeTypeEnum;
	};
}

export interface JointApplicationData {
	mainApplicant: ApplicantData;
	coApplicants: ApplicantData[];
	property?: Property;
	requestedFinancialParameters?: RequestedFinancialParameters;
}

// ==================== ELIGIBILITY RESULT TYPES ====================

export interface ReasonObject {
	code: string;
	message: string;
}

export interface EligibleResult {
	status: EligibilityStatusEnum.Eligible;
}

export interface PermanentNotEligibleResult {
	status: EligibilityStatusEnum.NotEligible;
	type: NotEligibleReasonType.PERMANENT;
	reason: ReasonObject;
}

export interface TemporaryNotEligibleResult {
	status: EligibilityStatusEnum.NotEligible;
	type: NotEligibleReasonType.TEMPORARY;
	reason: ReasonObject;
	daysBeforeReapply: number;
}

export type NotEligibleResult = PermanentNotEligibleResult | TemporaryNotEligibleResult;

export type EligibilityResult = EligibleResult | NotEligibleResult;

// ==================== FINANCIAL PARAMETERS ====================

export interface FinancialParameters {
	maximumBorrowingPower: number;
	maximumAffordablePropertyPrice: number;
	maximumEqualMonthlyInstallment: number;
	maximumTenure: number;
}

// ==================== API RESPONSE ====================

export interface JointApplicationSuccessEligibleResponse {
	applicationId: string;
	eligibility: EligibleResult;
	financialParameters: FinancialParameters;
}

export interface JointApplicationSuccessNotEligibleResponse {
	applicationId: string;
	eligibility: NotEligibleResult;
}

export type JointApplicationSuccessResponse =
	| JointApplicationSuccessEligibleResponse
	| JointApplicationSuccessNotEligibleResponse;

export interface ApiResponse<T> {
	ok: boolean;
	data: T;
	message?: string;
}

// ==================== ACTUAL API RESPONSE TYPES ====================
// These match the real API response format

export interface JointApplicationApplicant {
	id: string;
	profileId: string;
	nationalId: string;
	personalInformation: {
		birthDate: string;
		gender: string;
		maritalStatus: string;
		firstName: string;
		familyName: string;
		mobileNumber: string;
		age: number;
	};
	employmentData: any;
	creditReport: any;
	sectorId: string;
	persona: string;
	residentialType: string;
	numberOfDependents: number;
	numberOfDependentsInSchool: number;
	numberOfCarsOwned: number;
	expenses: Expenses;
	createdAt: string;
	updatedAt: string;
}

// ==================== NEW STAGE INTERFACES ====================

export interface ScorecardAttribute {
	attributeName: string;
	matched: boolean;
	value?: any;
	factorPoints: number;
	weight: number;
}

export interface ScorecardResult {
	success: boolean;
	message: string;
	score: number;
	riskGrade: string;
	attributes: ScorecardAttribute[];
}

export interface ScorecardStageResult {
	scorecards: Record<string, ScorecardResult>;
}

export interface PersonaMatchingResult {
	persona: {
		roi: number;
		maxFinanceAmount: number;
		approvingBody: string;
	};
	input: {
		nationality: string;
		sector: string;
		score: number;
	};
}

export interface PostPersonaMatchingResult {
	financeAmount: number;
	financeAmountIncludingProfit: number;
	emi: number;
	ltv: number;
	tenor: number;
	totalNewFinancialObligation: number;
	currentDbr: number;
	newCombinedDbr: number;
	disposableIncome: number;
	obligationsToDisposableIncomeRatio: number;
}

export interface RepaymentCheckResult {
	passedDBRCheck: boolean;
	repaymentCalculations: {
		financeAmount: number;
		financeAmountIncludingProfit: number;
		newMinDownPaymentPercent: number;
		newMinDownPayment: number;
		newMaximumPropertyPrice: number;
		emi: number;
		tenor: number;
	};
}

export interface KnockoutCheck {
	isEligible: boolean;
	value: number;
	min?: number;
	max?: number;
}

export interface KnockoutIndividualResult {
	success: boolean;
	isEligible: boolean;
	eligibilityErrors: any[];
	checks: Record<string, KnockoutCheck>;
	policy: Record<string, number>;
}

export interface KnockoutStageResult {
	knockoutChecksResults: Record<string, KnockoutIndividualResult>;
	knockoutResult: KnockoutIndividualResult;
}

export interface DeviationResult {
	isRejected: boolean;
	deviationsCount: number;
}

export interface DeviationStageResult {
	deviationResults: Record<string, DeviationResult>;
	financialDeviations: DeviationResult;
}

export interface PersonaRule {
	personaRuleId: string;
	personaId: string;
	personaName: string;
	creditRating: string;
	subsidyType: string;
	defaultContractType: string;
	snapshottedAnnualFlatRatePercent: string;
	effectiveAnnualFlatRatePercent: string;
	isOverridden: boolean;
	minFinanceAmount: string;
	maxFinanceAmount: string;
}

export interface TimeWindow {
	startsAt: string;
	endsAt: string;
}

export interface SectorConfig {
	sectorId: string;
	sectorName: string;
	riskLevels: string[];
	allocationPercent: string | null;
}

export interface Portfolio {
	portfolioId: string;
	name: string;
	status: string;
	matchedSectorConfig: SectorConfig;
	personaRules: PersonaRule[];
	productTypes: string[];
	contractTypes: string[];
	timeWindow: TimeWindow | null;
	minInvestmentAmount: string;
	maxInvestmentAmount: string | null;
	fundingCap: string | null;
}

export interface InvestmentPortfolioResult {
	totalMatches: number;
	eligibleMatches: number;
	matchedPersonaRule: PersonaRule;
	selectedPortfolioId: string;
	portfolios: Portfolio[];
}

export interface RejectionReason {
	code: string;
	message: string;
	daysBeforeReapply?: number;
	applicantId?: string;
}

export interface FinalDecisionResult {
	status: string;
	rejectionReasons: RejectionReason[];
	financeAmount: number;
	financeAmountPlusProfit: number;
	emi: number;
	tenor: number;
}

export interface StageData<T> {
	status: string;
	outputResult: {
		success: boolean;
		stageName: string;
		data: T;
		rejectionReasons?: RejectionReason[];
		metadata?: any;
	};
}

export interface JointApplicationResponse {
	applicationId: string;
	application: {
		id: string;
		status: string;
		mainApplicant: JointApplicationApplicant;
		coApplicants: JointApplicationApplicant[];
		property: Property;
		createdAt: string;
		updatedAt: string;
	};
	       stages: {
		       OPEN_BANKING?: StageData<any>;
		       NON_SALARIED_RAC?: StageData<any>;
		       RAC_POLICY?: StageData<any>;
		       CREDIT_BUREAU?: StageData<any>;
		       CONDITIONAL_APPROVAL?: StageData<any>;
		       INITIAL_ELIGIBILITY_CHECKS?: StageData<any>;
		       RESPONSIBLE_LENDING?: StageData<any>;
		       MAXIMUM_BORROWING_POWER?: StageData<any>;
		       POST_PROPERTY_SELECTION?: StageData<any>;
		       SCORECARD_EVALUATION?: StageData<ScorecardStageResult>;
		       PERSONA_MATCHING?: StageData<PersonaMatchingResult>;
		       POST_PERSONA_MATCHING?: StageData<PostPersonaMatchingResult>;
		       REPAYMENT_CALCULATIONS?: StageData<RepaymentCheckResult>;
		       KNOCKOUT?: StageData<KnockoutStageResult>;
		       DEVIATION?: StageData<DeviationStageResult>;
		       POST_DEVIATION_STAGE?: StageData<any>;
		       FINAL_ELIGIBILITY_CHECKS?: StageData<any>;
		       ADDITIONAL_INCOME?: StageData<any>;
		       INVESTMENT_PORTFOLIO?: StageData<InvestmentPortfolioResult>;
		       FINAL_DECISION?: StageData<FinalDecisionResult>;
	       };
}


